import { useState, useEffect, useRef } from 'react'
import { userFeedbackManager, buildMailtoUrl, type UserFeedback } from '../../utils/userFeedback'
import { MessageSquare, X } from 'lucide-react'

interface FeedbackFormProps {
  isOpen: boolean
  onClose: () => void
  initialType?: UserFeedback['type'] | undefined
  initialCategory?: string | undefined
  initialError?: string | undefined
}

export function FeedbackForm({
  isOpen,
  onClose,
  initialType = 'general_feedback',
  initialCategory = 'general',
  initialError,
}: FeedbackFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [feedbackId, setFeedbackId] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    type: initialType,
    title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
    description: initialError || '',
    category: initialCategory,
    priority: 'medium' as UserFeedback['priority'],
  })

  const contentRef = useRef<HTMLDivElement>(null)

  const feedbackTypes = [
    { value: 'bug_report', label: 'Bug Report' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'general_feedback', label: 'General Feedback' },
    { value: 'usability_issue', label: 'Usability Issue' },
    { value: 'performance_issue', label: 'Performance Issue' },
  ]

  const categories = ['general', 'ui_ux', 'performance', 'bug', 'feature_request', 'accessibility', 'calculations', 'battery_data']

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ]

  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: initialType,
        title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
        description: initialError || '',
        category: initialCategory,
        priority: 'medium',
      })
      setSubmitted(false)
      setFeedbackId(null)
      contentRef.current?.focus()
    }
  }, [isOpen, initialType, initialCategory, initialError])

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const payload = { ...formData, url: window.location.href }
      const id = userFeedbackManager.submitFeedback(payload)
      setFeedbackId(id)

      const mailtoUrl = buildMailtoUrl(payload)
      if (mailtoUrl) window.location.href = mailtoUrl

      setSubmitted(true)
    } catch (err) {
      console.error('Feedback submission failed:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field: string, value: string) =>
    setFormData(prev => ({ ...prev, [field]: value }))

  return (
    <div className="feedback-overlay" role="dialog" aria-modal="true" aria-label="Send feedback">
      <div className="feedback-modal" ref={contentRef} tabIndex={-1}>
        <div className="feedback-modal__header">
          <h2 className="feedback-modal__title">Send Feedback</h2>
          <button onClick={onClose} className="feedback-modal__close" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {submitted ? (
          <div className="feedback-modal__success">
            <p className="feedback-modal__thank-you">Thank you!</p>
            <p className="feedback-modal__success-desc">Your mail client should have opened. Send the pre-filled email to complete.</p>
            {feedbackId && <p className="feedback-modal__ref">Ref: {feedbackId}</p>}
            <button onClick={onClose} className="feedback-modal__close-btn">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="feedback-modal__form">
            <div className="feedback-modal__field">
              <label htmlFor="fb-type" className="feedback-modal__label">Type</label>
              <select id="fb-type" value={formData.type}
                onChange={e => handleChange('type', e.target.value)}
                className="feedback-modal__select">
                {feedbackTypes.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
              </select>
            </div>

            <div className="feedback-modal__field">
              <label htmlFor="fb-category" className="feedback-modal__label">Category</label>
              <select id="fb-category" value={formData.category}
                onChange={e => handleChange('category', e.target.value)}
                className="feedback-modal__select">
                {categories.map(c => (
                  <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </select>
            </div>

            <div className="feedback-modal__field">
              <p className="feedback-modal__label" id="fb-priority-label">Priority</p>
              <div className="feedback-modal__priority-row" role="radiogroup" aria-labelledby="fb-priority-label">
                {priorities.map(p => (
                  <button key={p.value} type="button"
                    role="radio"
                    aria-checked={formData.priority === p.value}
                    onClick={() => handleChange('priority', p.value)}
                    className={`feedback-modal__priority-btn ${formData.priority === p.value ? 'feedback-modal__priority-btn--active' : ''}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="feedback-modal__field">
              <label htmlFor="fb-title" className="feedback-modal__label">Title *</label>
              <input id="fb-title" type="text" required
                value={formData.title}
                onChange={e => handleChange('title', e.target.value)}
                placeholder="Brief summary"
                className="feedback-modal__input" />
            </div>

            <div className="feedback-modal__field">
              <label htmlFor="fb-description" className="feedback-modal__label">Description *</label>
              <textarea id="fb-description" rows={5} required
                value={formData.description}
                onChange={e => handleChange('description', e.target.value)}
                placeholder="Please describe in detail..."
                className="feedback-modal__textarea" />
            </div>

            <div className="feedback-modal__actions">
              <button type="button" onClick={onClose} className="feedback-modal__cancel-btn">Cancel</button>
              <button type="submit"
                disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
                className="feedback-modal__submit-btn">
                {isSubmitting ? 'Opening mail...' : 'Send Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

interface FeedbackButtonProps {
  onOpen: (type?: UserFeedback['type'], category?: string, error?: string) => void
}

export function FeedbackButton({ onOpen }: FeedbackButtonProps) {
  return (
    <button
      onClick={() => onOpen()}
      className="feedback-fab"
      aria-label="Send feedback"
      title="Send feedback"
    >
      <MessageSquare size={22} />
    </button>
  )
}
